/// <reference path="./mammoth-browser.d.ts" />
// Word / HTML → Markdown for the pdf-editor import flow.
//
// Both formats reach the PDF through the EXISTING markdown pipeline:
// convert to HTML (mammoth for .docx, the file itself for .html), then
// HTML → Markdown (turndown), then hand the markdown to ImportMdView,
// which lexes it, themes it and lays it out exactly as a .md import.
// That is a deliberate trade: a browser can't reproduce Word's own
// layout engine, so instead of a broken imitation the user gets clean,
// re-styleable structure — headings, lists, tables, links and images —
// under the same themes as every other import.
//
// Both libraries load lazily (mammoth ~200 KiB, turndown ~30 KiB) so
// they stay out of the editor bundle until a document is actually
// picked. turndown needs a DOM: real in the browser, @mixmark-io/domino
// under node (its own dependency), so this module is unit-testable.

export const DOCX_ACCEPT = [
  ".docx",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
].join(",");

export const HTML_ACCEPT = [".html", ".htm", ".xhtml", "text/html"].join(",");

const DOCX_EXT_RE = /\.docx$/i;
const HTML_EXT_RE = /\.(x?html?|htm)$/i;

export function isDocxFile(f: File): boolean {
  return DOCX_EXT_RE.test(f.name);
}

export function isHtmlFile(f: File): boolean {
  return HTML_EXT_RE.test(f.name);
}

/** Strip the source extension for the imported tab's name. */
export function docBaseName(name: string): string {
  return name.replace(DOCX_EXT_RE, "").replace(HTML_EXT_RE, "");
}

export interface DocConversion {
  markdown: string;
  /** Conversion warnings worth surfacing (unsupported Word styles…). */
  messages: string[];
  imageCount: number;
}

/** Images beyond this are dropped — a 300-image Word file would blow
 *  the layout step up long before the PDF is useful. */
const MAX_IMAGES = 48;
const MAX_IMAGE_BYTES = 6 * 1024 * 1024;

// ───────────────────────── HTML → Markdown ─────────────────────────

/** Turndown, plus the rules it lacks out of the box that Word and web
 *  HTML lean on hardest: tables and strikethrough. */
async function makeTurndown() {
  const { default: TurndownService } = await import("turndown");
  const td = new TurndownService({
    headingStyle: "atx",
    bulletListMarker: "-",
    codeBlockStyle: "fenced",
    emDelimiter: "*",
    strongDelimiter: "**",
  });

  td.addRule("strikethrough", {
    filter: ["del", "s"],
    replacement: (content: string) => (content ? `~~${content}~~` : ""),
  });

  // Word writes underline as <u>; markdown has no underline, and
  // dropping the tag silently would lose the text, so keep the words.
  td.addRule("underline", {
    filter: ["u", "ins"],
    replacement: (content: string) => content,
  });

  // Pipe tables. Turndown ships no table support; without this a Word
  // table's cells collapse into one run-on paragraph.
  td.addRule("table", {
    filter: "table",
    replacement: (_content: string, node: unknown) => {
      const table = node as {
        rows?: ArrayLike<{ cells?: ArrayLike<{ innerHTML?: string }> }>;
      };
      const rows = Array.from(table.rows ?? []);
      if (rows.length === 0) return "";
      const cellText = (html: string): string =>
        td
          .turndown(html || "")
          // A cell must stay on one line, and a literal pipe would
          // split the column.
          .replace(/\s*\n+\s*/g, " ")
          .replace(/\|/g, "\\|")
          .trim();
      const grid = rows.map((r) =>
        Array.from(r.cells ?? []).map((c) => cellText(c.innerHTML ?? ""))
      );
      const width = grid.reduce((n, r) => Math.max(n, r.length), 0);
      if (width === 0) return "";
      const pad = (r: string[]) => {
        const out = r.slice(0, width);
        while (out.length < width) out.push("");
        return out;
      };
      const [head, ...body] = grid;
      const lines = [
        `| ${pad(head!).join(" | ")} |`,
        `| ${Array.from({ length: width }, () => "---").join(" | ")} |`,
        ...body.map((r) => `| ${pad(r).join(" | ")} |`),
      ];
      return `\n\n${lines.join("\n")}\n\n`;
    },
  });

  return td;
}

/** Drop the parts of a document that carry no printable content, so
 *  turndown doesn't emit their raw text. */
function stripNonContent(html: string): string {
  return html
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/<(script|style|noscript|template|head)\b[^>]*>[\s\S]*?<\/\1>/gi, "")
    .replace(/<\/?(html|body)\b[^>]*>/gi, "");
}

export async function htmlToMarkdown(html: string): Promise<string> {
  const td = await makeTurndown();
  return td.turndown(stripNonContent(html)).trim();
}

// ───────────────────────── DOCX → Markdown ─────────────────────────

export async function docxToMarkdown(bytes: ArrayBuffer): Promise<DocConversion> {
  // The browser bundle is self-contained (its own zip/XML stack) and
  // avoids mammoth's node-only entry points — see mammoth-browser.d.ts.
  const mammoth = await import("mammoth/mammoth.browser.js");

  let imageCount = 0;
  let skippedImages = 0;
  const convertImage = mammoth.images.imgElement(async (image) => {
    if (imageCount >= MAX_IMAGES) {
      skippedImages += 1;
      return { src: "" };
    }
    try {
      const base64 = await image.read("base64");
      // base64 length ≈ 4/3 of the byte count.
      if ((base64.length * 3) / 4 > MAX_IMAGE_BYTES) {
        skippedImages += 1;
        return { src: "" };
      }
      imageCount += 1;
      return { src: `data:${image.contentType ?? "image/png"};base64,${base64}` };
    } catch {
      skippedImages += 1;
      return { src: "" };
    }
  });

  const result = await mammoth.convertToHtml(
    { arrayBuffer: bytes },
    {
      convertImage,
      // Word's built-in heading styles already map to h1–h6; these add
      // the quote/caption styles mammoth leaves as plain paragraphs.
      styleMap: [
        "p[style-name='Quote'] => blockquote:fresh",
        "p[style-name='Intense Quote'] => blockquote:fresh",
        "p[style-name='Title'] => h1:fresh",
        "p[style-name='Subtitle'] => h2:fresh",
        "p[style-name='Caption'] => p > em:fresh",
      ],
    }
  );

  const markdown = await htmlToMarkdown(result.value);

  const messages: string[] = [];
  const seen = new Set<string>();
  for (const m of result.messages ?? []) {
    const text = m.message?.trim();
    if (!text || seen.has(text)) continue;
    seen.add(text);
    messages.push(text);
    if (messages.length >= 20) break;
  }
  if (skippedImages > 0) {
    messages.unshift(
      `${skippedImages} image${skippedImages === 1 ? "" : "s"} skipped (too large, or over the ${MAX_IMAGES}-image limit).`
    );
  }

  return { markdown, messages, imageCount };
}
